import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AdvancedFilters } from './types';
import './FilterPills.scss';

interface FilterPill {
  filterKey: keyof AdvancedFilters;
  label: string;
  /** Canonical value — identity used for removal. Never shown resolved. */
  value: string;
  /** Display text — resolved to a label for TOC, defaults to value otherwise. */
  display: string;
}

interface FilterPillsProps {
  filters: AdvancedFilters;
  onRemove: (filterKey: keyof AdvancedFilters, value: string) => void;
  onClearAll: () => void;
  /**
   * Optional key -> display-label map for TOC pills (customer-typed spelling,
   * not the canonical lowercase key). Omit and TOC pills show the raw key,
   * same as every other filter — this only ever affects the `tocs` filterKey.
   */
  tocLabels?: Record<string, string>;
}

const FilterPills: FC<FilterPillsProps> = ({ filters, onRemove, onClearAll, tocLabels }) => {
  const { t } = useTranslation();

  // Build flat list of pills from all active filters
  const pills: FilterPill[] = [];

  const labelMap: Record<keyof AdvancedFilters, string> = {
    carrierIds: t('cpi.advancedFilters.carrier'),
    fuelTypes: t('cpi.advancedFilters.fuelType'),
    modalities: t('cpi.advancedFilters.modality'),
    emissionSources: t('cpi.advancedFilters.emissionSource'),
    shipperIds: t('cpi.advancedFilters.shipper'),
    tocs: t('cpi.advancedFilters.toc'),
  };

  (Object.keys(labelMap) as (keyof AdvancedFilters)[]).forEach((key) => {
    filters[key].forEach((value) => {
      const display = key === 'tocs' ? (tocLabels?.[value] ?? value) : value;
      pills.push({ filterKey: key, label: labelMap[key], value, display });
    });
  });

  if (pills.length === 0) return null;

  return (
    <div className="filter-pills">
      <div className="filter-pills__list">
        {pills.map((pill) => (
          <span
            key={`${pill.filterKey}-${pill.value}`}
            className="filter-pills__pill"
            title={`${pill.label}: ${pill.display}`}
          >
            <span className="filter-pills__label">{pill.label}:</span>
            <span className="filter-pills__value">{pill.display}</span>
            <button
              type="button"
              className="filter-pills__remove"
              onClick={() => onRemove(pill.filterKey, pill.value)}
              aria-label={`Remove ${pill.label}: ${pill.display}`}
            >
              &times;
            </button>
          </span>
        ))}
      </div>
      <button type="button" className="filter-pills__clear-all" onClick={onClearAll}>
        {t('cpi.advancedFilters.clearAll')}
      </button>
    </div>
  );
};

export default FilterPills;
